import { Elements } from '@stripe/react-stripe-js'
import { Suspense, useContext, useState } from 'react'
import { useLocation } from 'react-router'
import { useOnboardingSheet } from '@/components/onboarding-sheet/context'
import { BillingOnlyWizard } from '@/components/payment-method-gate/billing-only-wizard'
import { PaymentMethodGateContext } from '@/components/payment-method-gate/context'
import {
  BillingOnlyFallback,
  OnboardingFallback,
} from '@/components/payment-method-gate/fallback'
import { NoAccessMessage } from '@/components/payment-method-gate/no-access-message'
import { OnboardingWizard } from '@/components/payment-method-gate/onboarding-wizard'
import {
  useCompanyBilling,
  useIdCompany,
  useRBAC,
  useStripeElements,
} from '@/lib/hooks'
import type { ReactNode } from 'react'

const BYPASS_PREFIXES = ['/billing', '/my-account']

export const PaymentMethodGateProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const { hasCompany, hasPaymentMethod, hasBillingDetails } =
    useCompanyBilling()
  const [isSkipped, setIsSkipped] = useState(false)

  const isGated =
    hasCompany && (!hasPaymentMethod || !hasBillingDetails) && !isSkipped

  return (
    <PaymentMethodGateContext.Provider
      value={{ isGated, isSkipped, setIsSkipped }}
    >
      {children}
    </PaymentMethodGateContext.Provider>
  )
}

export const PaymentMethodGateContent = ({
  children,
}: {
  children: ReactNode
}) => {
  const context = useContext(PaymentMethodGateContext)
  const { open: openOnboarding } = useOnboardingSheet()
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const location = useLocation()
  const stripePromise = useStripeElements()
  const { hasPaymentMethod } = useCompanyBilling()

  const isGated = context?.isGated ?? false
  const isSuperadmin = rbac.kinsta().superadmin.can()
  const canUpdateBilling = rbac.company(idCompany).billing.update.can()
  const isBypassRoute = BYPASS_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  )

  if (!isGated || isBypassRoute) {
    return children
  }

  const onSkip = () => {
    context?.setIsSkipped(true)
    openOnboarding('cta')
  }

  const onSuccess = () => {
    openOnboarding('cta')
  }

  if (!canUpdateBilling) {
    return (
      <NoAccessMessage
        reason={hasPaymentMethod ? 'billing-details' : 'payment-method'}
        isSuperadmin={isSuperadmin}
        onSkip={onSkip}
      />
    )
  }

  if (hasPaymentMethod) {
    return (
      <Suspense fallback={<BillingOnlyFallback />}>
        <BillingOnlyWizard
          isSuperadmin={isSuperadmin}
          onSkip={onSkip}
          onSuccess={onSuccess}
        />
      </Suspense>
    )
  }

  return (
    <Elements stripe={stripePromise}>
      <Suspense fallback={<OnboardingFallback />}>
        <OnboardingWizard
          isSuperadmin={isSuperadmin}
          onSkip={onSkip}
          onSuccess={onSuccess}
        />
      </Suspense>
    </Elements>
  )
}
